import React, { useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { confirmAlert } from 'react-confirm-alert';
import cn from 'classnames';
import './BrandOnboardingCompetitorsPanel.scss';

import { addCompetitorBrand, deleteCompetitorBrand } from '../../../Actions/BrandActions';

import { fetchAlgoliaIndex } from '../../../Helpers/search_helpers';
import { getPrettyNumber, generalizeNumber } from '../../../Helpers/formatting';
import { getBrand, getCompetitorBrands } from '../../../Helpers/user_helpers';
import { LIMITED_SIMILAR_BRANDS_COUNT } from '../../Settings/Panels/BrandPanels/CompetitorBrandSettingsPanel';

import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ConfirmPrompt from '../../General/ConfirmPrompt';
import InfiniteScrollSelect from '../../General/InfiniteScrollSelect';
import BrandOnboardingProgressBar from '../BrandOnboardingProgressBar';

const BrandOnboardingCompetitorsPanel = props => {
  const { brandOnboarding, user } = props;
  const brand = getBrand(user);
  const { giftedValue, automatedGifts } = props.featuredStats?.lookbookStats || {};

  // Searching State
  const [isLoadingBrands, setIsLoadingBrands] = useState(false);
  const [algoliaOffset, setAlgoliaOffset] = useState(0);
  const [searchHasNextPage, setSearchHasNextPage] = useState(true);

  // Competitor Brands State
  const brandCompetitors = getCompetitorBrands(user);
  const applicationCompetitors = brandOnboarding?.application?.competitors || [];
  const [competitorBrands, setCompetitorBrands] = useState(brandCompetitors.length ? brandCompetitors : applicationCompetitors);
  const canAddMore = competitorBrands.length < LIMITED_SIMILAR_BRANDS_COUNT;
  const onPreviousClick = () => props.goToPreviousPanel();
  const onNextClick = () => {
    if (props.isLoading) return;

    if (competitorBrands.length === 0) return window.ALERT.error('Please add at least one competitor brand before proceeding.');
    props.goToNextPanel();
  };

  const loadMoreAlgoliaBrands = async (searchTerm, isNewSearch) => {
    if (!searchHasNextPage && !isNewSearch) return [];
    setIsLoadingBrands(true);

    let results = [];
    const offset = isNewSearch ? 0 : algoliaOffset;
    const PAGE_LENTGH = 20;

    try {
      const merchantsAlgoliaIndex = fetchAlgoliaIndex(!window.__IS_PROD__ ? 'merchants_staging' : 'merchants_production');
      const algoliaResp = await merchantsAlgoliaIndex.search(searchTerm || '', {
        ...(offset ? { offset } : {}),
        length: PAGE_LENTGH
      });

      results = algoliaResp.hits;
      const formattedResults = results.map(r => {
        const sublabels = [];

        if (r.promoter_count) sublabels.push(`${getPrettyNumber(r.promoter_count)} promoters`);
        if (r.domain) sublabels.push(r.domain);

        return {
          value: r.name,
          label: r.name,
          domain: r.domain,
          subLabel: sublabels.join(' • '),
          isDisabled: !r.domain || competitorBrands.find(competitorBrand => competitorBrand.domain === r.domain)
        };
      });

      setAlgoliaOffset(offset + PAGE_LENTGH);
      if (results.length < PAGE_LENTGH) setSearchHasNextPage(false);
      else setSearchHasNextPage(true);
      return formattedResults;
    } catch (error) {
      console.error('Error on Algolia Lookup: ', error);
      window.ALERT.error('There was an issue searching for brands. Please try again.');
    } finally {
      setIsLoadingBrands(false);
    }

    return [];
  };

  const handleCompetitorBrandAdd = async option => {
    if (!option || props.isLoading) return;

    const formattedCompetitorBrand = { Brand_id: brand?.id, name: option.label || option.name, domain: option.domain };
    const brandAlreadyAdded = competitorBrands.find(
      competitorBrand => competitorBrand.domain === formattedCompetitorBrand.domain || competitorBrand.name === formattedCompetitorBrand.name
    );
    if (brandAlreadyAdded) return window.ALERT.warn('This brand has already been added as a competitor.');
    else if (!canAddMore) return window.ALERT.warn(`You can only add up to ${LIMITED_SIMILAR_BRANDS_COUNT} brands as competitors.`);

    const res = await props.addCompetitorBrand(formattedCompetitorBrand);
    if (res.error) window.ALERT.error('There was an issue adding your similar brand. Please try again.');
    else window.ALERT.success(`${option.label || option.name} has been added as a competitor.`);

    setCompetitorBrands([...competitorBrands, formattedCompetitorBrand]);
    return true;
  };

  const confirmManualCompetitorBrandAdd = async newOptionName => {
    confirmAlert({
      customUI: ({ onClose }) => (
        <ConfirmPrompt
          header='Configure Similar Brand'
          subheader='Please provide the following information to configure your custom similar brand.'
          onCancel={onClose}
          customInputFields={[
            { display: 'Name', placeholder: 'Brand', value: 'name', isSingleLine: true, preloaded: newOptionName },
            { display: 'Domain', placeholder: 'brand.com', value: 'domain', isSingleLine: true }
          ]}
          onSubmitAwait={handleCompetitorBrandAdd}
          submitMustReturnTrueToClose={true}
        />
      )
    });
  };

  const handleCompetitorBrandDelete = async competitorBrand => {
    const res = await props.deleteCompetitorBrand(competitorBrand);
    if (res.error) window.ALERT.error('There was an issue removing your similar brand. Please try again.');
    else window.ALERT.success(`${competitorBrand.name} has been removed as a competitor.`);

    setCompetitorBrands(competitorBrands.filter(brand => brand.domain !== competitorBrand.domain));
  };

  return (
    <div className='brand-onboarding-competitor-brands-panel-outer'>
      <div className='brand-onboarding-competitor-brands-panel-inner'>
        <div className='brand-onboarding-split-container-outer'>
          <div className='brand-onboarding-split-container-inner'>
            <div className='brand-onboarding-graphic-container-outer'>
              <div className='brand-onboarding-graphic-background'>
                <img className='brand-onboarding-graphic-background-image' src={props.backgroundImage} alt='name panel graphic' />
                <div className='brand-onboarding-graphic-background-overlay' />
              </div>

              <div className='brand-onboarding-graphic-container-inner'>
                <div className='brand-onboarding-graphic-container-stat'>
                  <div className='brand-onboarding-graphic-container-stat-label'>Our Numbers</div>
                  <div className='brand-onboarding-graphic-container-stat-number'>{props.featuredStat.number}+</div>
                  <div className='brand-onboarding-graphic-container-stat-description'>{props.featuredStat.description}</div>
                </div>

                <BrandOnboardingProgressBar
                  goToPanel={props.goToPanel}
                  sections={props.sections}
                  activeSectionName={props.activeSectionName}
                  activePanelName={props.activePanelName}
                />
              </div>
            </div>

            <div className='brand-onboarding-input-container-outer'>
              <div className='brand-onboarding-input-container-number-marker'>{props.panelNumber}</div>

              <div className={cn('brand-onboarding-input-container-inner', { isLoading: props.isLoading })}>
                <div className='brand-onboarding-title-text'>Brand Details</div>
                <div className='brand-onboarding-subtitle-text'>Please provide us with the following information and assets.</div>

                <div className='brand-onboarding-input-label'>Brand Competitors</div>
                <div className='brand-onboarding-input-sublabel'>
                  Add up to {LIMITED_SIMILAR_BRANDS_COUNT} brands you consider in your competitive set, or brands that you consider yourself similar
                  to?
                </div>

                {canAddMore && (
                  <InfiniteScrollSelect
                    className='brand-search-select'
                    autoFocus={false}
                    openMenuOnFocus={true}
                    closeMenuOnSelect={true}
                    onSelectOptions={handleCompetitorBrandAdd}
                    loadMoreOptions={loadMoreAlgoliaBrands}
                    menuHeight={'170px'}
                    isLoading={isLoadingBrands}
                    placeholder={'Search for a brand'}
                    isOptionDisabled={option => !!option.isDisabled}
                    onNewOptionClick={confirmManualCompetitorBrandAdd}
                  />
                )}

                {!!competitorBrands?.length && (
                  <div className='brand-onboarding-competitor-brands-list'>
                    {competitorBrands.map(competitorBrand => (
                      <div
                        className='brand-onboarding-competitor-brands-list-item'
                        onClick={() => setCompetitorBrands(competitorBrands.filter(brand => brand.domain !== competitorBrand.domain))}
                        key={competitorBrand.domain}
                      >
                        <div className='name'>{competitorBrand.name}</div>
                        <FontAwesomeIcon icon={faTimes} onClick={() => handleCompetitorBrandDelete(competitorBrand)} />
                      </div>
                    ))}
                  </div>
                )}

                <div className='brand-onboarding-pagination-buttons'>
                  <button className='brand-onboarding-button stretch' onClick={onPreviousClick}>
                    Back
                  </button>
                  <button className={cn('brand-onboarding-button primary', { disabled: props.isLoading })} onClick={onNextClick}>
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

BrandOnboardingCompetitorsPanel.propTypes = {
  brandOnboarding: PropTypes.object.isRequired,
  addCompetitorBrand: PropTypes.func.isRequired,
  deleteCompetitorBrand: PropTypes.func.isRequired,

  goToPreviousPanel: PropTypes.func.isRequired,
  goToStep: PropTypes.func.isRequired,
  goToNextPanel: PropTypes.func.isRequired,
  panels: PropTypes.array.isRequired,
  panelNumber: PropTypes.number.isRequired
};

const mapStateToProps = state => {
  const { brandOnboarding, user } = state;
  return { brandOnboarding, user };
};

export default connect(mapStateToProps, {
  addCompetitorBrand,
  deleteCompetitorBrand
})(withRouter(BrandOnboardingCompetitorsPanel));
